import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * SlashLogo. The HOL H with the diagonal gold slash rendered as SVG so we can
 * animate the slash stroke on load, hover, and section dividers.
 *
 * Respects `prefers-reduced-motion`:
 *  - No initial draw animation.
 *  - No idle redraw loop.
 *  - Renders the static final logo state.
 *
 * Props:
 *  - size: px number, default 40
 *  - animateOnMount: draw slash on first mount (used on nav + hero)
 *  - idleRedraw: replay the slash draw every ~24s while visible (nav mark)
 *  - slashOnly: render just the diagonal slash (used as section divider / progress)
 */
export function SlashLogo({
  size = 40,
  animateOnMount = false,
  idleRedraw = false,
  slashOnly = false,
  title = "HOL",
}) {
  const prefersReducedMotion = useReducedMotion();
  const shouldAnimate = animateOnMount && !prefersReducedMotion;
  const shouldIdleRedraw = idleRedraw && !prefersReducedMotion;

  // Idle redraw tick. Toggles a key so framer-motion restarts the pathLength
  // draw at a slow cadence. Suppressed entirely under reduced motion.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!shouldIdleRedraw) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 24000);
    return () => clearInterval(id);
  }, [shouldIdleRedraw]);

  const draw = shouldAnimate
    ? {
        initial: { pathLength: 0, opacity: 0 },
        animate: { pathLength: 1, opacity: 1 },
        transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.05 },
      }
    : { initial: false };

  if (slashOnly) {
    return (
      <motion.svg
        role="presentation"
        aria-hidden="true"
        width={size}
        height={size}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <motion.line
          key={`slash-${tick}`}
          x1="18" y1="82" x2="82" y2="18"
          stroke="#D4AF37" strokeWidth="3.5" strokeLinecap="round"
          {...draw}
        />
      </motion.svg>
    );
  }

  return (
    <motion.svg
      key={`logo-${tick}`}
      role="img"
      aria-label={title}
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Outer gold ring */}
      <motion.circle
        cx="50" cy="50" r="46"
        stroke="#D4AF37" strokeWidth="2.5" fill="transparent"
        {...draw}
      />
      {/* H uprights */}
      <motion.line x1="34" y1="26" x2="34" y2="74" stroke="#D4AF37" strokeWidth="3.2" strokeLinecap="round" {...draw} />
      <motion.line x1="66" y1="26" x2="66" y2="74" stroke="#D4AF37" strokeWidth="3.2" strokeLinecap="round" {...draw} />
      {/* H crossbar */}
      <motion.line x1="34" y1="50" x2="66" y2="50" stroke="#D4AF37" strokeWidth="3.2" strokeLinecap="round" {...draw} />
      {/* Diagonal slash. The motion signature. */}
      <motion.line
        x1="20" y1="78" x2="80" y2="22"
        stroke="#D4AF37" strokeWidth="4" strokeLinecap="round"
        initial={shouldAnimate ? { pathLength: 0, opacity: 0 } : false}
        animate={shouldAnimate ? { pathLength: 1, opacity: 1 } : {}}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.55 }}
      />
    </motion.svg>
  );
}

/**
 * SlashDivider. Horizontal section divider that draws the diagonal slash across
 * the container width when it scrolls into view. Under reduced motion the
 * divider appears fully drawn without the stroke animation.
 */
export function SlashDivider() {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    return (
      <div className="slash-divider" aria-hidden="true">
        <svg viewBox="0 0 1200 80" preserveAspectRatio="none">
          <line
            x1="0" y1="70" x2="1200" y2="10"
            stroke="#D4AF37" strokeOpacity="0.55" strokeWidth="1.2" strokeLinecap="round"
          />
        </svg>
      </div>
    );
  }

  return (
    <div className="slash-divider" aria-hidden="true">
      <motion.svg
        viewBox="0 0 1200 80"
        preserveAspectRatio="none"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.4 }}
      >
        <motion.line
          x1="0" y1="70" x2="1200" y2="10"
          stroke="#D4AF37" strokeOpacity="0.55" strokeWidth="1.2" strokeLinecap="round"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        />
      </motion.svg>
    </div>
  );
}
